import { Link } from 'react-router-dom';

import { toast } from '@/components/ui';
import { revokeCookieConsent } from '@/lib/cookieConsent';

/**
 * Footer — pie de página premium de Mi Boleta (tema oscuro, 4 columnas),
 * emulando la estructura del footer de First Class. Fondo navy del menú lateral
 * (`#0A1B3D`) + texto claro para contraste. Se apila en móvil (grid responsive).
 *
 * Marca: se usa el MARK (`/mi-boleta-mark.png`, símbolo que contrasta sobre navy)
 * + wordmark "Mi Boleta" en blanco — mismo criterio que el `Brand` del Sidebar
 * (el `mi-boleta-logo.png` tiene el wordmark en azul oscuro, ilegible sobre navy).
 */

type SocialLink = { label: string; href: string; icon: JSX.Element };

const SOCIALS: SocialLink[] = [
  {
    label: 'Instagram',
    href: '#',
    icon: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
      </>
    ),
  },
  {
    label: 'Facebook',
    href: '#',
    icon: <path d="M14 9h3V6h-3a4 4 0 0 0-4 4v2H8v3h2v6h3v-6h2.5l.5-3H13v-2a1 1 0 0 1 1-1z" />,
  },
  {
    label: 'X',
    href: '#',
    icon: <path d="M4 4l7 8m0 0l7 8M11 12L4 20M11 12l7-8" />,
  },
  {
    label: 'WhatsApp',
    href: '#',
    icon: (
      <path d="M4 20l1.4-4A8 8 0 1 1 9 19.5L4 20zM9 9.5c0 3 2.5 5.5 5.5 5.5M9 9.5c0-.6.4-1 1-1M14.5 15c.6 0 1-.4 1-1" />
    ),
  },
];

// Categorías del sidebar de Mi Boleta (mismas etiquetas y rutas).
const NAV_LINKS: { label: string; to: string }[] = [
  { label: 'Inicio', to: '/' },
  { label: 'Eventos', to: '/eventos' },
  { label: 'Categorías', to: '/eventos/categorias' },
  { label: 'Ciudades', to: '/eventos/ciudades' },
  { label: 'Membresías', to: '/memberships' },
  { label: 'Experiencias', to: '/experiences' },
  { label: 'Flash sales', to: '/flash-sales' },
];

/**
 * Enlaces de ayuda. **Ya no queda ninguno en `href="#"`**: los tres apuntan a
 * páginas reales.
 *
 * Son `<Link>` y no `<a href>` porque son rutas de la aplicación: con un enlace
 * normal el navegador recargaría la página entera y tiraría el carrito que vive
 * en memoria.
 */
const HELP_ROUTES: { label: string; to: string }[] = [
  { label: 'Preguntas frecuentes', to: '/faq' },
  { label: 'Términos y condiciones', to: '/terminos' },
  { label: 'Política de privacidad', to: '/privacidad' },
];

function ColumnTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-white/90">{children}</h3>
  );
}

export function Footer(): JSX.Element {
  const year = new Date().getFullYear();

  /**
   * Revoca el consentimiento y hace reaparecer el banner.
   *
   * Se avisa con un `toast` porque el banner sale **al pie de la pantalla**, y
   * quien toca esto está mirando el pie de la página, que puede estar a varias
   * pantallas de distancia. Sin el aviso, la acción parecería no haber hecho
   * nada.
   */
  function handleCookiePreferences(): void {
    revokeCookieConsent();
    toast.info({
      title: 'Preferencias de cookies',
      description: 'Podés volver a elegir en el aviso que aparece abajo.',
    });
  }

  function handleNewsletter(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    // Simulado — no hay backend de newsletter todavía.
    toast.info({
      title: '¡Listo!',
      description: 'Te avisaremos con lo mejor en eventos y experiencias.',
    });
    e.currentTarget.reset();
  }

  return (
    <footer className="mt-16 bg-[#0A1B3D] text-white/80">
      <div className="container-fc py-12">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Columna 1 — Marca */}
          <div>
            <div className="flex items-center gap-2">
              <img
                src="/mi-boleta-mark.png"
                alt="Mi Boleta"
                className="h-9 w-9 flex-none object-contain"
              />
              <span className="font-display text-lg font-semibold text-white">Mi Boleta</span>
            </div>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/70">
              Experiencias a otro nivel
            </p>
            <div className="mt-5 flex items-center gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white/80 transition-colors hover:bg-[#2563FF] hover:text-white"
                >
                  <svg
                    aria-hidden="true"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {s.icon}
                  </svg>
                </a>
              ))}
            </div>
          </div>

          {/* Columna 2 — Navegación */}
          <nav aria-label="Navegación del pie">
            <ColumnTitle>Navegación</ColumnTitle>
            <ul className="space-y-2.5 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/70 transition-colors hover:text-white">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Columna 3 — Ayuda / Contacto */}
          <div>
            <ColumnTitle>Ayuda</ColumnTitle>
            <ul className="space-y-2.5 text-sm">
              {HELP_ROUTES.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-white/70 transition-colors hover:text-white">
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                {/*
                  Es un BOTÓN, no un enlace: no lleva a ninguna parte, ejecuta
                  una acción sobre el estado guardado. Marcarlo como enlace
                  haría que un lector de pantalla anunciara un destino que no
                  existe, y que un clic con el botón central abriera una pestaña
                  en blanco.
                */}
                <button
                  type="button"
                  onClick={handleCookiePreferences}
                  className="text-left text-white/70 transition-colors hover:text-white"
                >
                  Preferencias de cookies
                </button>
              </li>
            </ul>
            <div className="mt-5 space-y-1 text-sm text-white/60">
              <p>Cali, Colombia</p>
              <p>
                <a href="mailto:soporte@miboleta.co" className="transition-colors hover:text-white">
                  soporte@miboleta.co
                </a>
              </p>
            </div>
          </div>

          {/* Columna 4 — Newsletter */}
          <div>
            <ColumnTitle>Newsletter</ColumnTitle>
            <p className="text-sm text-white/70">Recibe lo mejor en tu correo</p>
            <form className="mt-4 flex flex-col gap-2" onSubmit={handleNewsletter}>
              <label htmlFor="footer-newsletter-email" className="sr-only">
                Correo electrónico
              </label>
              <input
                id="footer-newsletter-email"
                type="email"
                required
                autoComplete="email"
                placeholder="tu@correo.com"
                className="w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/40 outline-none transition-colors focus:border-[#2563FF]"
              />
              <button
                type="submit"
                className="rounded-lg bg-[#2563FF] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1e4fd6]"
              >
                Suscribirme
              </button>
            </form>
          </div>
        </div>

        {/* Barra inferior */}
        <div className="mt-10 flex flex-col gap-2 border-t border-white/10 pt-6 text-xs text-white/50 md:flex-row md:items-center md:justify-between">
          <p>&copy; {year} Mi Boleta — un producto de First Class</p>
          <p>
            Desarrollado por <span className="font-bold text-white">Softnexus</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
